'use client';

import { ActionButton } from '@/components/ui/ActionButton';

interface StepLayoutProps {
  heading: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  onBack?: () => void;
  showBack?: boolean;
  submitLabel?: string;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  darkBackground?: boolean;
  footer?: React.ReactNode;
}

/**
 * Common layout wrapper for wizard step components.
 * Provides consistent heading, description, form structure, and navigation buttons.
 */
export function StepLayout({
  heading,
  description,
  children,
  onSubmit,
  onBack,
  showBack = false,
  submitLabel = 'Continue',
  isSubmitting = false,
  submitDisabled = false,
  darkBackground = false,
  footer,
}: StepLayoutProps) {
  const textColor = darkBackground ? 'text-white' : 'text-[#292722]';

  return (
    <div className='mx-auto w-full max-w-[800px] px-4 py-8'>
      <form onSubmit={onSubmit} className='flex flex-col gap-[40px]'>
        <div className='flex flex-col items-center gap-[24px] text-center xl:gap-[40px]'>
          <h1
            className={`font-serif text-[27px] leading-[115%] font-light xl:text-[36px] xl:leading-[130%] ${textColor}`}
          >
            {heading}
          </h1>
          {description && (
            <p
              className={`font-sans text-[18px] leading-[130%] font-normal xl:text-[21px] ${textColor}`}
            >
              {description}
            </p>
          )}
        </div>

        {children}

        <div className='flex flex-col gap-[16px] md:flex-row md:gap-[48px]'>
          {showBack && (
            <ActionButton
              label='Back'
              variant='secondary'
              type='button'
              onClick={onBack}
              className='hidden flex-1 md:order-1 md:block'
            />
          )}
          <ActionButton
            label={isSubmitting ? 'Loading...' : submitLabel}
            variant='primary'
            type='submit'
            disabled={isSubmitting || submitDisabled}
            className='w-full flex-1 md:order-2 md:w-auto'
          />
        </div>

        {footer}
      </form>
    </div>
  );
}
